import { match, P } from 'ts-pattern'

import type { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'

import { TASK_EXECUTORS } from 'Automations/automations.constants'

type AutomationExecution = Dashboardv99990101Api.Automations.AutomationExecution
type AutomationExecutionTask =
  Dashboardv99990101Api.Automations.AutomationExecutionTask

/**
 * Where a quote approval run has got to, as far as the person who pressed
 * publish is concerned.
 *
 *   PUBLISHED     the rules waved it through and the quote is out
 *   NEEDS_REVIEW  it's sitting with a person now
 *   REJECTED      the run finished without doing either
 *   PENDING       still going; ask again in a moment
 */
export type ApprovalOutcome =
  | 'PUBLISHED'
  | 'NEEDS_REVIEW'
  | 'REJECTED'
  | 'PENDING'

/**
 * Reads one automation execution and says which of those it is.
 *
 * The two executors are the same ones the simulation matches on to predict
 * where a quote will end up - this is the same question asked of a run that has
 * actually happened, rather than of the workflow definition.
 *
 * Publishing is only reported once its task has completed, because that task is
 * what makes the quote public. A review, though, counts the moment its task
 * starts: the task stays in progress for as long as it waits on a person, so
 * waiting for it to complete would mean waiting for the review itself.
 */
export const readApprovalOutcome = (
  execution: AutomationExecution | undefined
): ApprovalOutcome => {
  if (!execution) return 'PENDING'

  const hasPublished = execution.tasks.some(
    task => runsExecutor(task, TASK_EXECUTORS.publishQuote) && isFinished(task)
  )
  if (hasPublished) return 'PUBLISHED'

  const isReviewTaskPending = execution.tasks.some(
    task => runsExecutor(task, TASK_EXECUTORS.reviewQuote) && isUnderway(task)
  )
  if (isReviewTaskPending) return 'NEEDS_REVIEW'

  // Nothing published and nobody asked, but the run is over - so the rules
  // turned this quote down, or the run fell over on the way.
  return match(execution.status)
    .with('COMPLETED', 'FAILED', 'TERMINATED', () => 'REJECTED' as const)
    .otherwise(() => 'PENDING' as const)
}

const runsExecutor = (task: AutomationExecutionTask, executor: string) =>
  task.inputData.taskExecutorConfiguration?.executor === executor

const isFinished = (task: AutomationExecutionTask) =>
  task.status === 'COMPLETED'

/**
 * Started, and not over. `COMPLETED_WITH_ERRORS` is deliberately not here - a
 * review task that ended that way isn't waiting on anyone.
 */
const isUnderway = (task: AutomationExecutionTask) =>
  match(task.status)
    .with(P.union('IN_PROGRESS', 'SCHEDULED'), () => true)
    .otherwise(() => false)
